import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { formatPhoneDisplay } from '@/pages/agent-detail/lib';
import { cn } from '@/shared/lib/cn';
const FEED_FILTERS = [
    { id: 'all', label: 'Все события' },
    { id: 'calls', label: 'Звонки' },
    { id: 'tasks', label: 'Задачи' },
    { id: 'bt', label: 'БТ' },
    { id: 'other_events', label: 'Другие мероприятия' },
    { id: 'client', label: 'С клиентом' },
    { id: 'notices', label: 'Уведомления' },
];
function pluralEvents(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11)
        return 'событие';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
        return 'события';
    }
    return 'событий';
}
function formatFeedDateTime(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return d.toLocaleString('ru-RU', {
        timeZone: 'Asia/Novosibirsk',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
function formatDuration(seconds) {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0)
        return null;
    const n = Math.floor(seconds);
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = n % 60;
    const parts = [];
    if (h)
        parts.push(`${h} ч`);
    if (m)
        parts.push(`${m} мин`);
    if (s || (!h && !m))
        parts.push(`${s} сек`);
    return parts.join(' ');
}
function callPhoneLabel(phone) {
    if (!phone?.trim())
        return 'Телефон не указан';
    const digits = phone.replace(/\D/g, '').replace(/^8/, '7');
    if (digits.length === 11 && digits.startsWith('7')) {
        return formatPhoneDisplay(digits);
    }
    if (digits.length === 10) {
        return formatPhoneDisplay(`7${digits}`);
    }
    return phone.trim();
}
function callStatusLabel(call) {
    if (call.answered)
        return 'Состоялся';
    if (call.missed || !call.answered) {
        return call.direction === 'INBOUND' ? 'Пропущен' : 'Без ответа';
    }
    return 'Результат не указан';
}
function callDurationLine(call) {
    const parts = [];
    const talk = formatDuration(call.talkSeconds);
    const total = formatDuration(call.totalSeconds);
    if (talk && (call.answered || (call.talkSeconds ?? 0) > 0)) {
        parts.push(`Разговор ${talk}`);
    }
    if (total && (total !== talk || parts.length === 0)) {
        parts.push(parts.length ? `всего ${total}` : `Вызов ${total}`);
    }
    return parts.length ? parts.join(' · ') : 'Длительность не указана';
}
function activityTitle(type, note) {
    if (note?.trim())
        return note.trim();
    if (type === 'NOTICE_SENT')
        return 'Уведомление';
    if (type === 'BUS_TOUR')
        return 'Брокер-тур';
    if (type === 'EVENT_ATTENDED')
        return 'Мероприятие';
    if (type === 'AGENCY_CONTRACT')
        return 'Договор с АН';
    if (type === 'MANUAL_NOTE')
        return 'Заметка';
    if (type === 'PRIORITY_LIST')
        return 'Список приоритета';
    return type;
}
function activityKindLabel(kind) {
    if (kind === 'bt')
        return 'Брокер-тур';
    if (kind === 'other_events')
        return 'Мероприятие';
    return 'Уведомление';
}
function taskStateLabel(state) {
    if (state === 'OPEN')
        return 'Открыта';
    if (state === 'IN_PROGRESS')
        return 'В работе';
    if (state === 'BLOCKED')
        return 'Заблокирована';
    if (state === 'DONE' || state === 'COMPLETED')
        return 'Завершена';
    if (state === 'CANCELLED')
        return 'Отменена';
    return state;
}
export function CycleEventFeed(props) {
    const { cycle, feed, filter, onFilterChange } = props;
    const active = cycle.status === 'ACTIVE';
    const items = useMemo(() => buildTimeline(cycle, feed), [cycle, feed]);
    const counts = useMemo(() => {
        const next = {
            all: items.length,
            calls: 0,
            tasks: 0,
            bt: 0,
            other_events: 0,
            client: 0,
            notices: 0,
        };
        for (const item of items) {
            if (item.kind === 'call')
                next.calls += 1;
            else if (item.kind === 'task')
                next.tasks += 1;
            else
                next[item.activityKind] += 1;
        }
        return next;
    }, [items]);
    const filtered = useMemo(() => {
        if (filter === 'all')
            return items;
        if (filter === 'calls')
            return items.filter((item) => item.kind === 'call');
        if (filter === 'tasks')
            return items.filter((item) => item.kind === 'task');
        return items.filter((item) => item.kind === 'activity' && item.activityKind === filter);
    }, [items, filter]);
    const periodText = cycle.period
        ? `${formatFeedDateTime(cycle.startedAt)} — ${formatFeedDateTime(cycle.closedAt ?? new Date().toISOString())}`
        : null;
    const filterHint = filter === 'calls'
        ? 'Звонки — по времени начала вызова'
        : filter === 'tasks'
            ? 'Задачи — по дате события'
            : 'События — по дате';
    return (_jsxs("section", { className: "rounded-[10px] border border-[#e5e9f1] bg-[var(--unity-color-elevated,#fff)] p-5 md:p-6", "aria-label": "\u041B\u0435\u043D\u0442\u0430 \u0441\u043E\u0431\u044B\u0442\u0438\u0439 \u0430\u0433\u0435\u043D\u0442\u0430", children: [_jsxs("div", { className: "mb-2 flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { className: "min-w-0 space-y-1", children: [_jsxs("p", { className: "text-[11px] uppercase tracking-wide text-muted-foreground", children: [cycle.displayNumber, " \u00B7", ' ', active ? 'Активное' : 'Завершённое', " \u0432\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u0435"] }), _jsx("h2", { className: "text-[21px] font-semibold tracking-tight text-foreground", children: "\u041B\u0435\u043D\u0442\u0430 \u0441\u043E\u0431\u044B\u0442\u0438\u0439" })] }), _jsxs("span", { className: "inline-flex rounded-md border border-[#e5e9f1] px-2.5 py-1 text-[11px] text-muted-foreground", children: [counts.all, " ", pluralEvents(counts.all)] })] }), periodText ? (_jsx("p", { className: "text-sm text-foreground", children: periodText })) : null, _jsx("p", { className: "mb-4 text-xs text-muted-foreground", children: "\u0422\u043E\u043B\u044C\u043A\u043E \u0441\u043E\u0431\u044B\u0442\u0438\u044F \u0437\u0430 \u0432\u0440\u0435\u043C\u044F \u044D\u0442\u043E\u0433\u043E \u0412\u043E\u0432\u043B\u0435\u0447\u0435\u043D\u0438\u044F \u00B7 \u041D\u043E\u0432\u043E\u0441\u0438\u0431\u0438\u0440\u0441\u043A" }), _jsx("div", { className: "mb-3 flex flex-wrap gap-2", "aria-label": "\u0424\u0438\u043B\u044C\u0442\u0440 \u043B\u0435\u043D\u0442\u044B \u0441\u043E\u0431\u044B\u0442\u0438\u0439", children: FEED_FILTERS.map((item) => {
                    const selected = filter === item.id;
                    return (_jsxs("button", { type: "button", "aria-pressed": selected, onClick: () => onFilterChange(item.id), className: cn('inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] transition-colors', selected
                            ? 'border-[#ddd8f2] bg-[#f7f5ff] text-primary'
                            : 'border-[#e5e9f1] bg-white text-[#5c5670] hover:border-[#d5d0e8]'), children: [item.label, _jsx("span", { className: cn('tabular-nums', selected ? 'text-primary' : 'text-[#9a96b0]'), children: counts[item.id] })] }, item.id));
                }) }), _jsxs("div", { className: "mb-4 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground", children: [_jsx("span", { children: "\u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u043D\u043E\u0432\u044B\u0435" }), _jsx("span", { children: filterHint })] }), filtered.length === 0 ? (_jsxs("div", { className: "rounded-lg border border-dashed border-[#e5e9f1] px-4 py-10 text-center", children: [_jsx("h3", { className: "text-sm font-medium text-foreground", children: items.length
                            ? 'В этой категории событий нет'
                            : 'За время Вовлечения событий пока нет' }), _jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: items.length
                            ? 'Выберите другой фильтр, чтобы увидеть остальные события.'
                            : 'Здесь появятся задачи, звонки, подтверждённые посещения и уведомления.' })] })) : (_jsx("ol", { className: "relative m-0 list-none space-y-3 p-0", children: filtered.map((item, index) => (_jsx(TimelineRow, { item: item, isLast: index === filtered.length - 1 }, item.kind === 'activity'
                    ? `activity-${item.id}`
                    : `${item.kind}-${item.data.id}`))) }))] }));
}
function buildTimeline(cycle, feed) {
    const rows = [];
    for (const call of feed?.calls ?? []) {
        rows.push({ kind: 'call', at: Date.parse(call.at), data: call });
    }
    for (const task of feed?.tasks ?? []) {
        rows.push({ kind: 'task', at: Date.parse(task.at), data: task });
    }
    for (const activity of cycle.activities ?? []) {
        let activityKind = null;
        if (activity.type === 'BUS_TOUR')
            activityKind = 'bt';
        else if (activity.type === 'EVENT_ATTENDED')
            activityKind = 'other_events';
        else if (activity.type === 'NOTICE_SENT')
            activityKind = 'notices';
        else
            continue;
        rows.push({
            kind: 'activity',
            at: Date.parse(activity.occurredAt),
            activityKind,
            id: activity.id,
            occurredAt: activity.occurredAt,
            occurredAtLocal: activity.occurredAtLocal,
            note: activity.note,
            type: activity.type,
        });
    }
    return rows
        .filter((row) => Number.isFinite(row.at))
        .sort((a, b) => b.at - a.at);
}
function TimelineRow(props) {
    const { item, isLast } = props;
    const missedCall = item.kind === 'call' &&
        !item.data.answered &&
        (item.data.missed || item.data.answered === false);
    return (_jsxs("li", { className: "relative flex gap-3", children: [_jsxs("div", { className: "relative flex w-9 shrink-0 flex-col items-center", children: [_jsx("span", { className: cn('z-[1] grid h-9 w-9 place-items-center rounded-full border text-primary', missedCall
                            ? 'border-[#f3d2b8] bg-[#fff4ea] text-[#c46a1b]'
                            : 'border-[#ddd8f2] bg-[#f4f2ff]'), "aria-hidden": true, children: _jsx(FeedIcon, { kind: item.kind === 'activity' ? item.activityKind : item.kind }) }), !isLast ? (_jsx("span", { className: "absolute top-9 bottom-[-12px] w-px bg-[#e5e9f1]", "aria-hidden": true })) : null] }), _jsxs("article", { className: "min-w-0 flex-1 rounded-[10px] border border-[#e5e9f1] bg-white p-3.5 md:p-4", children: [item.kind === 'call' ? _jsx(CallCard, { call: item.data }) : null, item.kind === 'task' ? _jsx(TaskCard, { task: item.data }) : null, item.kind === 'activity' ? (_jsx(ActivityCard, { kind: item.activityKind, occurredAt: item.occurredAt, occurredAtLocal: item.occurredAtLocal, note: item.note, type: item.type })) : null] })] }));
}
function CallCard(props) {
    const call = props.call;
    const inbound = call.direction === 'INBOUND';
    const outbound = call.direction === 'OUTBOUND';
    const missed = !call.answered && (call.missed || call.answered === false);
    const status = callStatusLabel(call);
    return (_jsxs("div", { className: "space-y-1.5 text-sm", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-2 text-[11px] text-muted-foreground", children: [_jsx("span", { children: inbound
                            ? 'Звонок от Агента'
                            : outbound
                                ? 'Звонок Агенту'
                                : 'Звонок' }), _jsx("time", { dateTime: call.at, children: formatFeedDateTime(call.at) })] }), _jsxs("h3", { className: "text-[15px] font-semibold leading-snug text-foreground", children: [inbound ? 'Входящий' : outbound ? 'Исходящий' : 'Направление не указано', ' ', "\u00B7 ", callPhoneLabel(call.phone)] }), _jsxs("p", { className: "text-[13px] text-foreground", children: [_jsx("strong", { className: cn('font-semibold', missed ? 'text-[#c46a1b]' : 'text-foreground'), children: status }), ' · ', callDurationLine(call)] }), _jsx("p", { className: "text-[12px] text-muted-foreground", children: call.hasRecording ? 'Запись в ЦРМ' : 'Без записи' }), _jsxs("details", { className: "pt-1 text-[12px] text-muted-foreground", children: [_jsx("summary", { className: "cursor-pointer select-none text-primary hover:underline", children: "\u041F\u043E\u0434\u0440\u043E\u0431\u043D\u043E\u0441\u0442\u0438 \u0437\u0432\u043E\u043D\u043A\u0430" }), _jsxs("div", { className: "mt-2 space-y-1 rounded-md bg-[#f7f8fc] px-3 py-2", children: [_jsxs("p", { children: ["ID ", call.id] }), _jsx("p", { children: inbound
                                    ? 'Входящий вызов от агента'
                                    : outbound
                                        ? 'Исходящий вызов агенту'
                                        : 'Направление не указано' }), call.hasRecording ? (_jsx("p", { children: "\u0412 \u0426\u0420\u041C \u0435\u0441\u0442\u044C \u0430\u0443\u0434\u0438\u043E\u0437\u0430\u043F\u0438\u0441\u044C. \u041F\u0440\u043E\u0441\u043B\u0443\u0448\u0438\u0432\u0430\u043D\u0438\u0435 \u2014 \u0432 \u0426\u0420\u041C \u043F\u0440\u043E\u0434\u0430\u0436." })) : (_jsx("p", { children: "\u0410\u0443\u0434\u0438\u043E\u0437\u0430\u043F\u0438\u0441\u044C \u0432 \u0426\u0420\u041C \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u0430." }))] })] })] }));
}
function TaskCard(props) {
    const task = props.task;
    return (_jsxs("div", { className: "space-y-1.5 text-sm", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-2 text-[11px] text-muted-foreground", children: [_jsxs("span", { children: ["\u0417\u0430\u0434\u0430\u0447\u0430", task.source === 'cabinet' ? ' · Кабинет' : ''] }), _jsx("time", { dateTime: task.at, children: formatFeedDateTime(task.at) })] }), _jsx("h3", { className: "text-[15px] font-semibold leading-snug text-foreground", children: task.title }), _jsxs("p", { className: "text-[13px] text-foreground", children: [taskStateLabel(task.state), task.dueAt
                        ? ` · до ${formatFeedDateTime(task.dueAt)}`
                        : ''] }), task.description ? (_jsx("p", { className: "text-[12px] text-muted-foreground", children: task.description })) : null, _jsx("p", { className: "text-[12px] text-muted-foreground", children: task.source === 'cabinet'
                    ? 'Задача кабинета'
                    : task.sourceTaskId
                        ? `Задача №${task.sourceTaskId}`
                        : 'Задача CRM' })] }));
}
function ActivityCard(props) {
    return (_jsxs("div", { className: "space-y-1.5 text-sm", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-2 text-[11px] text-muted-foreground", children: [_jsx("span", { children: activityKindLabel(props.kind) }), _jsx("time", { dateTime: props.occurredAt, children: props.occurredAtLocal || formatFeedDateTime(props.occurredAt) })] }), _jsx("h3", { className: "text-[15px] font-semibold leading-snug text-foreground", children: activityTitle(props.type, props.note) })] }));
}
function FeedIcon(props) {
    const common = {
        width: 16,
        height: 16,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 1.7,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        'aria-hidden': true,
    };
    if (props.kind === 'call') {
        return (_jsx("svg", { ...common, children: _jsx("path", { d: "M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.5-1.1a2 2 0 0 1 2.1-.4c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" }) }));
    }
    if (props.kind === 'task') {
        return (_jsxs("svg", { ...common, children: [_jsx("path", { d: "M9 11l3 3L22 4" }), _jsx("path", { d: "M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" })] }));
    }
    if (props.kind === 'notices') {
        return (_jsxs("svg", { ...common, children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("path", { d: "M14 2v6h6" })] }));
    }
    return (_jsxs("svg", { ...common, children: [_jsx("circle", { cx: "12", cy: "8", r: "4" }), _jsx("path", { d: "M4 20c1.5-4 6-6 8-6s6.5 2 8 6" })] }));
}
